/**
 * The Procedure tab: which version of the procedure you are looking at, and the
 * canvas drawing it.
 *
 * A procedure is one editable draft plus any number of immutable published
 * versions. Selection lives in the URL — `?version=5`, absent for the draft — so
 * the two read the same way and the back button walks between them.
 *
 * Versions are read-only and the service has no way to restore one, so both
 * "edit" and "publish" on a version are the same move underneath: write that
 * version's document into the draft, which is why both warn first.
 */

import { Alert, Button, Spin, message } from 'antd';
import { ReactFlowProvider } from '@xyflow/react';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { AOPDraft, AOPDraftPlan, AOPVersion } from '../../types/AOPTypes';
import { getEntityById, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { AGENT_PROCEDURES_CRUD_ACTION_CREATORS } from '../../actions/agent_procedures_actions';
import { versionKey } from '../../reducers/agent_procedure_versions_reducer';
import { useDraftSave } from './useDraftSave';
import {
	AOPDraftPatch,
	AOP_VERSIONS_FIRST_PAGE,
	fetchProcedureDraft,
	fetchProcedureVersion,
	fetchProcedureVersions,
	patchProcedureDraft,
	publishProcedure,
	writeWholeProcedureDraft,
} from '../../thunks/agent_procedures_thunks';
import { withUserType } from '../withUserType';
import { AOPProcedureBody } from './AOPProcedureBody';
import AOPEditVersionModal, { AOPVersionIntent } from './AOPEditVersionModal';
import AOPPublishModal from './AOPPublishModal';
import { isSameAsVersion } from './utils/AOPVersionUtils';
import './AOPBuilder.less';

/** `?version=5`, or null for the draft. */
const selectedVersionFrom = (search: string): number | null => {
	const raw = new URLSearchParams(search).get('version');

	if (raw === null) {
		return null;
	}

	const parsed = parseInt(raw, 10);
	return isNaN(parsed) ? null : parsed;
};

/*
  The collection the detail page fills with this procedure's scenario. Named
  here too because this tab reads it; the detail page is what writes it.
*/
const SCENARIO_TC = 'aopProcedureScenario';

const AOPProcedureTab: FC<any> = ({
	storePlan,
	readError,
	procedureId,
	draft,
	scenario,
	summaries,
	versionCount,
	versionsOffset,
	versionsByKey,
	isLoading,
	isSaving,
	userType,
	history,
	saveDraft,
	patchDraft,
	publish,
	loadDraft,
	loadVersions,
	loadMoreVersions,
	loadVersion,
}): React.ReactElement => {
	/*
	  The selected version, if reading it was refused.

	  A version is immutable, so one that cannot be read will not arrive on its
	  own — the screen has to say so rather than wait. Only the selected one is
	  remembered, because it is the only one anything asks about: a published
	  version that cannot be read costs the comparison against the draft, which
	  already reports "don't know" rather than guessing.
	*/
	const [unreadableVersion, setUnreadableVersion] = useState<number | null>(null);
	const [isLoadingMoreVersions, setIsLoadingMoreVersions] = useState(false);

	/*
	  The edit goes into the store and to the service at the same time.

	  Every edit used to build a whole plan from the store and send it, so two
	  committed before the first response landed both started from the same
	  unchanged draft and the second omitted the first — a lost edit, with
	  nothing said. And the canvas waited on the network to redraw, because
	  every render came from a response.

	  So an edit is written to `records[procedureId]` at once and sent after a
	  pause. The store is what the canvas draws, which is the whole point: a
	  copy held beside it had to be merged over the draft, handed back to a
	  screen that remounted, and kept from being overwritten by the answer to
	  the save that carried it. None of that exists when there is one copy, so
	  what decides when the service is told is a pause and a single waiting
	  slot rather than a queue.
	*/
	const {
		push: pushPlan,
		isSaving: isSavePending,
		error: saveError,
	} = useDraftSave<AOPDraftPlan>((plan) => patchDraft(procedureId, { plan }));

	const [isPublishing, setIsPublishing] = useState(false);
	const [confirmIntent, setConfirmIntent] = useState<AOPVersionIntent | null>(null);
	const [isAskingToPublish, setIsAskingToPublish] = useState(false);
	/** Raised by a refused publish, not by the last draft we happen to hold. */
	const [isShowingBlocked, setIsShowingBlocked] = useState(false);

	const selectedVersion = selectedVersionFrom(history.location.search);
	const publishedVersion = nullSafeGet('procedure.published_version', draft);
	// `nullSafeGet` yields undefined for a missing field while the service sends
	// null for "never published", so both have to count as unpublished.
	const hasPublished = publishedVersion !== null && publishedVersion !== undefined;

	// The version being shown, and the latest published one, are both needed: the
	// first to draw, the second to tell whether the draft has moved on. The detail
	// page loads both on mount, so this only fires for a version reached by a
	// link — a `?version=2` pasted in, say.
	const wanted = useMemo(() => {
		const versions: number[] = [];

		if (selectedVersion !== null) {
			versions.push(selectedVersion);
		}

		if (hasPublished && versions.indexOf(publishedVersion) < 0) {
			versions.push(publishedVersion);
		}

		return versions;
	}, [hasPublished, publishedVersion, selectedVersion]);

	const missing = wanted.filter((version) => !versionsByKey[versionKey(procedureId, version)]);

	/*
	  Reading an immutable document the store does not have yet is a
	  synchronisation with the service, not state derived from props, so it
	  belongs in an effect. A version that arrives drops out of `missing` and the
	  effect settles; one that was refused is skipped, which is also what makes
	  retrying it nothing more than forgetting the refusal.
	*/
	React.useEffect(() => {
		missing
			.filter((version) => version !== unreadableVersion)
			.forEach((version) =>
				loadVersion(procedureId, version).catch(() => {
					message.error(`Could not load v${version}.`);

					// Only the selected one. Remembering a refused published version
					// would mean two of them could be remembered one at a time, each
					// unblocking the other's read — a request loop. It is not retried
					// either way: `missing` still holds it, so these deps do not
					// change and the effect does not run again.
					if (version === selectedVersion) {
						setUnreadableVersion(version);
					}
				})
			);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [procedureId, missing.join(','), selectedVersion, unreadableVersion, loadVersion]);

	/**
	 * The next page of the history, from the picker.
	 *
	 * The offset comes from the store, which counts positions read rather than
	 * rows held — overlapping pages are merged away, and publishing rereads the
	 * first page. Deriving it here from either would eventually skip the middle
	 * of the history or reread its tail forever.
	 */
	const onLoadMoreVersions = useCallback(() => {
		setIsLoadingMoreVersions(true);
		loadMoreVersions(procedureId, versionsOffset)
			.catch(() => message.error('Could not load more versions.'))
			.then(() => setIsLoadingMoreVersions(false));
	}, [loadMoreVersions, procedureId, versionsOffset]);

	const latestPublished: AOPVersion = hasPublished
		? versionsByKey[versionKey(procedureId, publishedVersion)]
		: null;
	const selected: AOPVersion =
		selectedVersion === null ? null : versionsByKey[versionKey(procedureId, selectedVersion)];

	// Whether the draft holds anything the live version does not. The service
	// reports no such flag, so the two documents are compared here.
	/*
	  Three answers, not two: it differs, it matches, or we cannot say yet.

	  The live version's document is read separately and may not have arrived —
	  or may have failed outright, in which case it never will. Folding that
	  into "no unpublished changes" made the bar state that the draft matches
	  the published version on no evidence, which is the one thing it must not
	  say about an edit that has not been published.
	*/
	const hasUnpublishedChanges: boolean | null = !hasPublished
		? true
		: !latestPublished
		? null
		: !isSameAsVersion(nullSafeGet('prose', draft), nullSafeGet('plan', draft), latestPublished);

	const hrefFor = useCallback(
		(version: number | null) => {
			const base = `/${userType}/workflows/agent_operating_procedures/detail/${procedureId}/procedure`;
			return version === null ? base : `${base}?version=${version}`;
		},
		[procedureId, userType]
	);

	const onSelectVersion = useCallback(
		(version: number | null) => history.push(hrefFor(version)),
		[history, hrefFor]
	);

	/**
	 * Every edit writes the whole draft back. The service answers with the plan
	 * it stored plus fresh findings, so the canvas never has to guess whether
	 * what it is showing would validate.
	 *
	 * A rejected save used to be swallowed, which meant the canvas showed an
	 * edit the service had refused and the author only found out by reloading.
	 * It says so now — the canvas is still showing the change, and the point is
	 * that it has not been kept.
	 */
	const onChangePlan = useCallback(
		(plan: AOPDraftPlan) => {
			// Into the store at once, so the canvas redraws from it, and to the
			// service after the pause. The findings are left as they are until it
			// answers: they describe the plan before this edit, and clearing them
			// would read as problems solved.
			//
			// A failure already reported stays reported until a save gets through —
			// this edit has not been taken either — so `saveError` is left alone.
			storePlan(procedureId, plan);
			pushPlan(plan);
		},
		[procedureId, pushPlan, storePlan]
	);

	/**
	 * Ruling on one of the compiler's filled-in details. The plan does not
	 * change — the suggestion's status does — so the draft is written back as it
	 * stands with the id in `accept`.
	 */
	const onAcceptSuggestion = useCallback(
		(suggestionId: string) => {
			/*
			  Only the ruling: the plan does not change, so none is sent. Its
			  response carries the whole draft, but a draft write's plan is not
			  adopted — the store holds the author's — so one landing beside a plan
			  save can leave only the findings a beat behind, which they already are
			  by design.
			*/
			patchDraft(procedureId, { accept: [suggestionId] }).catch(() => undefined);
		},
		[patchDraft, procedureId]
	);

	/**
	 * Publishing lands you on what you just published, not back on the draft: the
	 * two are identical at that moment, and the version is the thing that now
	 * runs.
	 */
	const publishDraft = useCallback(
		(changeNote: string | null) => {
			setIsPublishing(true);

			/*
			  No flush. Publish is held while a save is outstanding or has failed —
			  the control says so and comes back when the service has the plan — so
			  by the time this runs there is nothing left to send.
			*/
			publish(procedureId, changeNote)
				.then((procedure) => {
					/*
					  The version is cut by now. These two only catch the screen up, so
					  their failure is swallowed rather than let into the catch below —
					  it used to reject into it and report "Could not publish this
					  draft" about a publish that had already succeeded.

					  Navigation waits for them even so. Leaving before they land puts
					  the picker on a version the store does not hold yet, and the
					  effect that reads versions it is missing then asks for the very
					  document this read is already bringing.
					*/
					return Promise.all([loadDraft(procedureId), loadVersions(procedureId)])
						.catch(() => undefined)
						.then(() => {
							const published = nullSafeGet('published_version', procedure);

							if (published !== null && published !== undefined) {
								history.push(hrefFor(published));
							}
						});
				})
				.catch((error) => {
					if (error === 'draft_not_publishable') {
						// Reread first: the findings in hand are from the last draft we
						// were given, and the service has just refused a newer one. The
						// modal names what is in the way, so it has to name the current
						// answer rather than the one that looked fine.
						loadDraft(procedureId)
							.catch(() => undefined)
							.then(() => setIsShowingBlocked(true));
					} else {
						message.error('Could not publish this draft.');
					}
				})
				.then(() => {
					setIsPublishing(false);
					setIsAskingToPublish(false);
				});
		},
		[history, hrefFor, loadDraft, loadVersions, procedureId, publish]
	);

	/**
	 * Bring a version back into the draft. Publish only ever cuts from the draft,
	 * so making an old version live means restoring it first and publishing that
	 * — which is also why it replaces whatever the draft held.
	 */
	const restoreSelectedVersion = useCallback(
		(intent: AOPVersionIntent) => {
			if (!selected) {
				return Promise.resolve();
			}

			setIsPublishing(intent === 'publish');

			/*
			  No flush: Edit is held while a save is outstanding, for the same reason
			  Publish is. Restoring replaces the draft wholesale, so an edit still on
			  its way would land after it and put part of the discarded plan back.
			*/
			/*
			  The version's own run limit travels with its prose and plan. Without
			  it the draft read as v`n` while running under whatever limit the draft
			  already had, and publishing that cut a version labelled as restored
			  from one document that was not the same document.
			*/
			return (
				saveDraft(procedureId, selected.prose, selected.plan, undefined, selected.max_run_seconds)
					.then(() => {
						if (intent === 'edit') {
							message.success(`v${selected.version} is now your draft.`);
							history.push(hrefFor(null));
							return undefined;
						}

						return publish(procedureId, `Restored v${selected.version}`)
							.then((procedure) =>
								/*
						  The version is cut by now. These two only catch the screen up, so
						  their failure is theirs — it used to reject into the catch below
						  and say "Could not restore that version" about a restore that had
						  already happened, leaving the author on the old version to try
						  again and cut a second identical one.

						  Navigation waits for them, as it does after a publish: arriving
						  ahead of them has the version picker ask for a document that is
						  already on its way.
						*/
								Promise.all([loadDraft(procedureId), loadVersions(procedureId)])
									.catch(() => message.error('Restored, but the page could not refresh.'))
									.then(() => {
										const published = nullSafeGet('published_version', procedure);

										if (published !== null && published !== undefined) {
											history.push(hrefFor(published));
										}
									})
							)
							.catch(() => {
								/*
						  The draft is already this version — `saveDraft` is what put it
						  there — and only cutting a version from it failed. Saying
						  "Could not restore that version" here, which the outer catch
						  did, was wrong twice: the restore happened, and it left the
						  author looking at the immutable version with no sign their
						  draft had been replaced underneath them.
						*/
								message.error(
									`v${selected.version} is now your draft, but publishing it failed. Try Publish again.`
								);
								history.push(hrefFor(null));
							});
					})
					// Only `saveDraft` reaches this now, which is the one failure that
					// really does mean nothing was restored.
					.catch(() => message.error('Could not restore that version.'))
					.then(() => {
						setIsPublishing(false);
						setConfirmIntent(null);
					})
			);
		},
		[history, hrefFor, loadDraft, loadVersions, procedureId, publish, saveDraft, selected]
	);

	/**
	 * The warning is about losing the draft, so it is skipped when there is
	 * nothing to lose — a draft already identical to this version restores to
	 * itself.
	 *
	 * Against the plan on screen, not the stored one. An edit still on its way
	 * to the service leaves the stored draft matching the version, so comparing
	 * that skipped the warning and `restoreSelectedVersion` flushed the edit and
	 * then wrote over it — the one thing the warning exists to prevent.
	 */
	const askThenRestore = useCallback(
		(intent: AOPVersionIntent) => {
			if (!selected) {
				return;
			}

			const draftIsThisVersion = isSameAsVersion(
				nullSafeGet('prose', draft),
				nullSafeGet('plan', draft),
				selected
			);

			if (draftIsThisVersion) {
				restoreSelectedVersion(intent);
				return;
			}

			setConfirmIntent(intent);
		},
		[draft, restoreSelectedVersion, selected]
	);

	if (isLoading && !draft) {
		return (
			<div className="flex justify-center p-l">
				<Spin tip="Loading procedure…" />
			</div>
		);
	}

	/*
	  A draft that failed to load is not a procedure with no steps.

	  The reducer clears `fetching` either way and stores nothing on a failure,
	  so both arrive here as an absent draft — and the invitation to describe one
	  is exactly the wrong thing to show somebody whose procedure is fine and
	  whose network is not.
	*/
	if (!draft && readError) {
		return (
			<div className="p-l">
				<Alert
					type="error"
					showIcon
					message="Could not load this procedure"
					description="Reload to try again. Nothing has been changed."
				/>
			</div>
		);
	}

	if (!draft || !draft.plan) {
		return (
			<div className="p-l">
				<Alert
					type="info"
					showIcon
					message="This procedure has no steps yet"
					description="Describe what Radar should do and generate its steps to get started."
				/>
			</div>
		);
	}

	// A version is rendered through the same shape as the draft so the canvas has
	// one input. It carries no findings — a version validated when it was
	// published and the service only re-checks a draft.
	/*
	  The draft as drawn: the local plan when edits are outstanding, the store's
	  otherwise. The findings come from the store either way — they describe the
	  plan the service last saw, which is the honest thing to show while it has
	  not seen this one yet.
	*/
	const shownDraft: AOPDraft =
		selectedVersion === null
			? draft
			: selected
			? {
					procedure: draft.procedure,
					prose: selected.prose,
					plan: selected.plan,
					// The version's own, not the draft's: the two can differ once the
					// draft has been edited since. Nothing draws it either way.
					max_run_seconds: selected.max_run_seconds,
					suggestions: [],
					violations: [],
					publishable: true,
			  }
			: null;

	if (!shownDraft) {
		// A version that was refused is not on its way, so the spinner would run
		// for as long as the tab stayed open. Say what happened, and offer both
		// ways out of it.
		if (unreadableVersion === selectedVersion) {
			return (
				<div className="p-l">
					<Alert
						type="error"
						showIcon
						message={`Could not load v${selectedVersion}`}
						description={
							<div className="flex flex-col items-start gap-s">
								<span className="bodyregular text-character-primary">
									That version could not be read. It may not exist, or the service may not have
									answered.
								</span>
								<div className="flex items-center gap-s">
									{/* Forgetting the refusal is the retry: the effect skips a version
									    it has been refused, so clearing that lets it read again. */}
									<Button onClick={() => setUnreadableVersion(null)}>Try again</Button>
									<Link to={hrefFor(null)}>Back to the draft</Link>
								</div>
							</div>
						}
					/>
				</div>
			);
		}

		return (
			<div className="flex justify-center p-l">
				<Spin tip={`Loading v${selectedVersion}…`} />
			</div>
		);
	}

	return (
		<ReactFlowProvider>
			<AOPProcedureBody
				draft={draft}
				shownDraft={shownDraft}
				summaries={summaries}
				versionCount={versionCount}
				isLoadingMoreVersions={isLoadingMoreVersions}
				onLoadMoreVersions={onLoadMoreVersions}
				selectedVersion={selectedVersion}
				publishedVersion={hasPublished ? publishedVersion : null}
				hasUnpublishedChanges={hasUnpublishedChanges}
				isReadOnly={selectedVersion !== null}
				refreshFailed={Boolean(readError) && Boolean(draft)}
				isBlocked={isShowingBlocked}
				onCloseBlocked={() => setIsShowingBlocked(false)}
				onAcceptSuggestion={onAcceptSuggestion}
				isPublishing={isPublishing}
				isSaving={isSaving}
				isSavePending={isSavePending}
				saveError={saveError}
				scenario={scenario}
				scenariosHref={`/${userType}/workflows/agent_operating_procedures/detail/${procedureId}/scenarios`}
				hrefFor={hrefFor}
				onSelectVersion={onSelectVersion}
				onChangePlan={onChangePlan}
				onPublish={
					selectedVersion === null
						? () => setIsAskingToPublish(true)
						: () => askThenRestore('publish')
				}
				onEdit={() => askThenRestore('edit')}
			/>
			{/*
			  Mounted on the flag that opens it, not kept alive behind it. The note
			  is the modal's own state, so a modal that stays mounted carries the
			  last version's note into the next one and it can be published as that
			  version's description.
			*/}
			{isAskingToPublish ? (
				<AOPPublishModal
					open
					version={hasPublished ? publishedVersion : null}
					isPublishing={isPublishing}
					isSavingDraft={isSaving}
					onCancel={() => setIsAskingToPublish(false)}
					onPublish={publishDraft}
				/>
			) : null}
			<AOPEditVersionModal
				open={confirmIntent !== null}
				intent={confirmIntent === null ? 'edit' : confirmIntent}
				version={selectedVersion}
				isWorking={isPublishing || isSaving}
				onCancel={() => setConfirmIntent(null)}
				onConfirm={() => restoreSelectedVersion(confirmIntent === null ? 'edit' : confirmIntent)}
			/>
		</ReactFlowProvider>
	);
};

const mapStateToProps = (state, ownProps) => {
	const procedureId = nullSafeGet('match.params.id', ownProps);
	const summariesByProcedure = nullSafeGetOrElse('agent_procedure_versions.summaries', state, {});
	// The Start block draws the scenario's trigger criteria. The detail page
	// loads the scenario list already, so this reads it rather than refetching.
	/*
	  Through the collection's ids, like the detail page and the Scenarios tab.
	  `records` is merged into and never pruned, so a scenario deleted in another
	  session survives a refetch that no longer returns it — and the Start block
	  would go on showing routing that is gone.
	*/
	const scenariosSlice = nullSafeGetOrElse('agent_scenarios', state, {});
	const scenarioRecords = nullSafeGetOrElse('records', scenariosSlice, {});
	const scenarioEntry = nullSafeGetOrElse('recordsIds', scenariosSlice[SCENARIO_TC], [] as string[])
		.map((id) => scenarioRecords[id])
		.filter((entry) => entry && entry.procedure && entry.procedure.id === procedureId)[0];

	return {
		procedureId,
		// The slice keeps every fetch error; the newest is the one that explains
		// why there is no draft to show.
		readError: nullSafeGetOrElse('agent_procedures.fetchErrors', state, []).slice(-1)[0],
		scenario: scenarioEntry ? scenarioEntry.scenario : null,
		draft: getEntityById(procedureId, state.agent_procedures, null),
		// Bracket access, not a `nullSafeGet` path: a procedure id is a uuid today
		// but the key is an identifier, not a dotted path.
		summaries: summariesByProcedure[procedureId] ? summariesByProcedure[procedureId] : [],
		// How many there are, which is not how many are held: the history pages.
		versionCount: nullSafeGetOrElse('agent_procedure_versions.counts', state, {})[procedureId],
		// Where the next page starts: positions read, not rows held.
		versionsOffset:
			nullSafeGetOrElse('agent_procedure_versions.offsets', state, {})[procedureId] || 0,
		versionsByKey: nullSafeGetOrElse('agent_procedure_versions.versions', state, {}),
		isLoading: nullSafeGetOrElse('agent_procedures.fetching', state, false),
		/*
		  A whole document on its way — restoring a version, or writing the first
		  draft — not the canvas's own autosave, which `useDraftSave` tracks and
		  reports as `isSavePending` beside this.
		*/
		isSaving: nullSafeGetOrElse('agent_procedures.updating', state, false),
	};
};

const mapDispatchToProps = (dispatch) => ({
	// Replaces the draft entire — restoring a version, and nothing else.
	saveDraft: (
		procedureId: string,
		prose: string,
		plan: AOPDraftPlan,
		accept?: string[],
		maxRunSeconds?: number
	) => dispatch(writeWholeProcedureDraft(procedureId, prose, plan, accept, maxRunSeconds)),
	// Changes what it names and leaves the rest as the service holds it.
	/*
	  The edit, into the store, before the service has been asked.

	  `fetchOneDeltaSuccess` merges a partial record into `records[id]`, which is
	  what makes this one line rather than a reducer of its own — the canvas
	  reads the draft from there, so writing the plan into it is the redraw.
	*/
	storePlan: (procedureId: string, plan: AOPDraftPlan) =>
		dispatch(AGENT_PROCEDURES_CRUD_ACTION_CREATORS.fetchOneDeltaSuccess({ id: procedureId, plan })),
	patchDraft: (procedureId: string, patch: AOPDraftPatch) =>
		dispatch(patchProcedureDraft(procedureId, patch)),
	publish: (procedureId: string, changeNote: string | null) =>
		dispatch(publishProcedure(procedureId, changeNote)),
	loadDraft: (procedureId: string) => dispatch(fetchProcedureDraft(procedureId)),
	/*
	  Hydrated: this screen draws a version and compares the draft against the
	  live one, so it wants the documents and not only the picker's labels. The
	  effect that reads a missing version still covers anything older than the
	  first page.
	*/
	loadVersions: (procedureId: string) =>
		dispatch(
			fetchProcedureVersions(procedureId, { hydrate: true, limit: AOP_VERSIONS_FIRST_PAGE })
		),
	/*
	  The next page of the history, asked for from the picker.

	  Not hydrated: the first page carries the documents this screen draws, and
	  somebody scrolling the picker is reading labels. A version they then
	  choose is read on its own by the effect that fetches what is missing.
	*/
	loadMoreVersions: (procedureId: string, offset: number) =>
		dispatch(fetchProcedureVersions(procedureId, { offset })),
	loadVersion: (procedureId: string, version: number) =>
		dispatch(fetchProcedureVersion(procedureId, version)),
});

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(AOPProcedureTab))
);
